import React, {
  createContext, useContext, useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { useUserContext } from 'store';
import { usePatientSummaryAPI } from 'apiServices/patientSummaryAPI';
import { useTimelineAPI } from 'apiServices/timelineAPI';
import MHDRequests from 'services/MHDRequests';
import { oids, features } from 'configs';
// import {
//   CNSisValid,
//   verifyCPF,
// } from 'utils/AuxiliarFunctions';
import { useDocumentReferenceAPI } from 'apiServices/documentReferenceAPI';
import { useDiagnosticReportAPI } from 'apiServices/diagnosticReportAPI';

const initialValue = {
  subject: {
    id: '',
    organizationId: '',
    token: '',
  },
  resource: {
    name: '',
    gender: '',
    dateOfBirth: '',
    healthNumber: '',
    id: '',
    purposeOfUse: 'Atendimento',
  },
};

const EHRContext = createContext(initialValue);

export function EHRContextProvider({ children, urlInput }) {
  const [user] = useUserContext();
  const [subject, setSubject] = useState(initialValue.subject);
  const [resource, setResource] = useState(initialValue.resource);

  const [diagnosticReport, , loadingDiagnosticReport, refetchDiagnosticReport] = useDiagnosticReportAPI({
    subjectId: subject.id,
    resourceId: resource.id,
    purposeOfUse: resource.purposeOfUse,
    subjectToken: subject.token,
    organizationId: subject.organizationId,
  });
  const [prescription, , loadingPrescription, refetchPrescription] = useDocumentReferenceAPI({
    subjectId: subject.id,
    resourceId: resource.id,
    purposeOfUse: resource.purposeOfUse,
    subjectToken: subject.token,
    organizationId: subject.organizationId,
    category: 'prescricao',
  });
  const [certificates, , loadingCertificates, refetchCertificates] = useDocumentReferenceAPI({
    subjectId: subject.id,
    resourceId: resource.id,
    purposeOfUse: resource.purposeOfUse,
    subjectToken: subject.token,
    organizationId: subject.organizationId,
    category: 'atestado',
  });
  const [eventSummary, , loadingEventSummary, refetchEventSummary] = useDocumentReferenceAPI({
    subjectId: subject.id,
    resourceId: resource.id,
    purposeOfUse: resource.purposeOfUse,
    subjectToken: subject.token,
    organizationId: subject.organizationId,
    category: 'event-summary',
  });
  const [patientSummary, , loadingSummary, refetchSummary] = usePatientSummaryAPI({
    subjectId: subject.id,
    resourceId: resource.id,
    purposeOfUse: resource.purposeOfUse,
    subjectToken: subject.token,
    organizationId: subject.organizationId,
  });
  const [patientTimeline, , loadingTimeline, refetchTimeline] = useTimelineAPI({
    subjectId: subject.id,
    resourceId: resource.id,
    purposeOfUse: resource.purposeOfUse,
    subjectToken: subject.token,
    organizationId: subject.organizationId,
  });

  async function setPatient({
    resourceId,
    subjectToken,
    organizationId,
    purposeOfUse,
    subjectId,
    subjectRole,
  }) {
    let patientOID;
    if (resourceId.length === 11) patientOID = oids.cpf;
    else patientOID = oids.cns;
    // if (CNSisValid(resourceId)) patientOID = oids.cns;
    // else if (verifyCPF(resourceId)) patientOID = oids.cpf;
    try {
      const patientJSON = await MHDRequests.getPatientJson({
        patientId: resourceId,
        subjectToken,
        organizationId,
        purposeOfUse,
        subjectId,
        subjectRole,
        patientOID,
      });
      setSubject({
        ...subject,
        id: subjectId,
        organizationId,
        token: subjectToken,
      });
      setResource({
        name: patientJSON.name.find((nameObj) => nameObj.use === 'official').text,
        gender: patientJSON.gender,
        dateOfBirth: patientJSON.birthDate,
        healthNumber: patientJSON.identifier.find((idObj) => (
          idObj.system.includes(oids.cns) && idObj.use === 'official'
        ))?.value || '',
        id: patientJSON.identifier.find((idObj) => idObj.system.includes(oids.cpf) || idObj.system.includes(oids.cns))?.value || '',
        purposeOfUse,
      });
    } catch (err) {
      setResource({
        name: 'Não encontrado',
        gender: 'Não encontrado',
        dateOfBirth: 'Não encontrado',
        healthNumber: 'Não encontrado',
        id: 'Não encontrado',
        purposeOfUse,
      });
    }

    if (features.myReports) {
      refetchEventSummary();
      refetchCertificates();
      refetchPrescription();
      refetchDiagnosticReport();
    } else {
      refetchEventSummary();
      refetchCertificates();
      refetchPrescription();
      refetchDiagnosticReport();
      refetchSummary();
      refetchTimeline();
    }
  }

  function setLoggedUserAsPatient() {
    setPatient({
      resourceId: user.cpf,
      subjectId: user.cpf,
    });
  }

  useEffect(() => {
    if (urlInput) {
      const urlString = window.location.href;
      if (urlString.includes('?')) {
        const urlParams = urlString.split('?')[1].split('&');
        const paramKeyValue = {};
        urlParams.forEach((param) => {
          const key = param.split('=')[0];
          const value = param.split('=')[1];
          paramKeyValue[key] = value;
        });
        setPatient({
          resourceId: paramKeyValue['resource-id'],
          organizationId: paramKeyValue['organization-id'],
          purposeOfUse: paramKeyValue['purposeofuse'],
          subjectId: paramKeyValue['subject-id'],
          subjectToken: paramKeyValue['token'],
          subjectRole: paramKeyValue['role'],
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loading = loadingSummary && loadingTimeline && loadingDiagnosticReport && loadingCertificates && loadingPrescription && loadingEventSummary;
  const contextValue = {
    ehrState: { subject, resource },
    diagnosticReport,
    prescription,
    eventSummary,
    certificates,
    patientSummary,
    patientTimeline,
    loading,
    setPatient,
    setLoggedUserAsPatient,
  };
  return (
    <EHRContext.Provider value={contextValue}>
      {children}
    </EHRContext.Provider>
  );
}

export function useEHRContext() {
  return useContext(EHRContext);
}

EHRContextProvider.propTypes = {
  children: PropTypes.element.isRequired,
  urlInput: PropTypes.bool,
};

EHRContextProvider.defaultProps = {
  urlInput: false,
};
